import { useEffect } from "react"
import { DepartmentType, PositionType } from "@/types"
import { zodResolver } from "@hookform/resolvers/zod"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import Cookies from "js-cookie"
import { useForm } from "react-hook-form"
import * as z from "zod"

import { editPosition } from "@/lib/api/billing"
import { useToast } from "@/hooks/use-toast"
import { Button } from "@/components/ui/button"
import { DropdownMenuSeparator } from "@/components/ui/dropdown-menu"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Header } from "@/components/header"

import { Icons } from "../icons"
import { Card } from "../ui/card"
import { Input } from "../ui/input"

const formSchema = z.object({
  name: z.string().min(1, "Name  is required"),
  department: z.string().min(1, "Department is required"),
})

export default function EditPositionForm({
  positionData,
  setShowEditModal,
}: {
  positionData: PositionType
  setShowEditModal: React.Dispatch<React.SetStateAction<boolean>>
}) {
  const { toast } = useToast()
  const queryClient = useQueryClient()

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: positionData?.name || "",
      department: positionData?.department || "",
    },
  })

  useEffect(() => {
    if (positionData) {
      form.reset({
        name: positionData.name,
        department: positionData.department,
      })
    }
  }, [positionData, form.reset])

  const mutation = useMutation({
    mutationFn: async (data: PositionType) => {
      if (positionData?.id) {
        await editPosition(positionData?.id, data)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["positionDetails"],
      })
      toast({
        description: "Position updated successfully.",
      })
      setShowEditModal(false)
    },
    onError: () => {
      toast({
        title: "Something went wrong!",
        description: "Please try again.",
        variant: "destructive",
      })
    },
  })

  function handleSubmit(values: z.infer<typeof formSchema>) {
    mutation.mutate(values as PositionType)
  }
  return (
    <div className="px-2">
      <Header
        subroutes={[
          {
            route: "/user-role-management/position",
            label: "Position",
          },
        ]}
      >
        Edit Position
      </Header>
      <DropdownMenuSeparator className="mt-4" />
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(handleSubmit)}
          className="grid grid-cols-1 gap-4 lg:container md:max-w-[1000px]"
        >
          <Card className="my-4 p-6">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Name<span className="text-red-600">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </Card>
          <div className="flex flex-row justify-end gap-2 md:mr-4">
            <Button
              variant="outline"
              type="button"
              onClick={() => {
                setShowEditModal(false)
              }}
              className="w-28"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={mutation.isPending}
              className="w-28"
            >
              {" "}
              {mutation.isPending && (
                <Icons.loader className="mr-2 animate-spin" />
              )}
              Save Changes
            </Button>
          </div>
        </form>
      </Form>
    </div>
  )
}
